import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { hashKey } from '@tanstack/react-query'

type PendingInvalidation = {
  subscribers: number
  timer?: ReturnType<typeof setTimeout>
}
const clients = new WeakMap<QueryClient, Map<string, PendingInvalidation>>()

/** Share one refresh window across all mounted consumers of a scoped query. */
export function subscribeToQueryInvalidation(
  client: QueryClient,
  scope: string,
  queryKey: QueryKey,
) {
  let entries = clients.get(client)
  if (!entries) {
    entries = new Map()
    clients.set(client, entries)
  }
  const key = hashKey([scope, queryKey])
  let pending = entries.get(key)
  if (!pending) {
    pending = { subscribers: 0 }
    entries.set(key, pending)
  }
  const entry = pending
  entry.subscribers++
  let disposed = false
  return {
    schedule() {
      if (disposed || entry.timer !== undefined) return
      entry.timer = setTimeout(() => {
        entry.timer = undefined
        void client.invalidateQueries({ queryKey, exact: true })
      }, 800)
    },
    dispose() {
      if (disposed) return
      disposed = true
      if (--entry.subscribers === 0) {
        clearTimeout(entry.timer)
        entries.delete(key)
      }
    },
  }
}
